import React, { FC, useEffect } from 'react'

import { Modal } from 'is24-corecss'
import { useSelector } from 'react-redux'
import { Intent } from '../../../@types/intent'
import './UserConsentModal.scss'
import { RealEstateType } from '../../../@types/realEstateType'
import { ReduxState } from '../../../store/reducers'
import { IntegrationType } from '../../../@types/config'
import { WizardFlowType } from '../../../@types/wizardFlowType'

interface UserConsentModalProps {
  visibility: boolean
  setVisibility: (isVisible: boolean) => void
}

const MaklerLink = () => (
  <a
    href='https://www.immobilienscout24.de/anbieter/?cmp_id=10-05083&cmp_name=residential_yellowpages&cmp_position=residential_realtorleadengine&cmp_creative=rle_widget_yellowpages'
    target='_blank'
    rel='noreferrer noopener'
    data-testid='makler-link'
  >
    Makler
  </a>
)

const AnbieterLink = ({ displayText }: { displayText?: string }) => (
  <a
    href='https://www.immobilienscout24.de/anbieter/'
    target='_blank'
    rel='noreferrer noopener'
    data-testid='anbieter-link'
  >
    {displayText ?? 'kooperierenden Immobilienexperten'}
  </a>
)

const DataProtectionInformationLink = ({ params }: { params?: string }) => (
  <a
    target='_blank'
    rel='noreferrer noopener'
    href={`https://www.immobilienscout24.de/agb/datenschutz.html${params ? `?${params}` : ''}`}
    data-testid='data-protection-information-link'
  >
    Datenschutz-Information
  </a>
)

export const UserConsentModal: FC<UserConsentModalProps> = (props) => {
  const {
    lead: { intent, realEstateType, extendedBuyerLead, isConsultationAccepted },
    config: { integrationType },
    flow: { wizardFlowType },
  } = useSelector((state: ReduxState) => state)
  const visibility = props.visibility

  const getLegalText = (
    realEstateType?: RealEstateType,
    intent?: Intent,
    isConsultationAccepted?: boolean
  ): React.JSX.Element => {
    if (realEstateType === RealEstateType.COMMERCIAL && integrationType != IntegrationType.IMMOVERKAUF24) {
      return consentUserIntentForCommercial
    }
    if (integrationType === IntegrationType.IMMOVERKAUF24) {
      return consentUserIntentForImmoverkauf
    }
    if (wizardFlowType === WizardFlowType.SOLAR) {
      return consentUserSolarIntent
    }
    switch (intent) {
      case Intent.MAKLER_SALE:
      case Intent.MAYBE_MAKLER_SALE:
        return consentUserIntentSell
      case Intent.BUY:
        return extendedBuyerLead ? consentUserIntentExtendedBuy : consentUserIntentBuy
      case Intent.LET:
        return consentUserIntentRentOut
      case Intent.MAYBE_MAKLER_LET:
      case Intent.MAKLER_LET:
        return consentUserIntentRentOutWithOrMaybeWithRealtor
      case Intent.INDEPENDENT_LET:
        return consentUserIntentRentOutWithoutRealtor
      case Intent.RENT:
        return consentUserIntentRent
      case Intent.INDEPENDENT_SALE:
        return isConsultationAccepted
          ? consentUserIntentIndependentSellAndConsultation
          : consentUserIntentIndependentSell
      case Intent.PARTIAL_SALE:
        return consentUserIntentIndependentSellPartial
      case Intent.INDEPENDENT_WITH_OFFER_SALE:
        return consentUserIntentIndependentSellWithOffer
      default:
        return <div></div>
    }
  }

  const getTitle = (intent?: Intent, isConsultationAccepted?: boolean) => {
    if (wizardFlowType == WizardFlowType.SOLAR) {
      return 'Hinweis Solarempfehlung'
    }
    switch (intent) {
      case Intent.INDEPENDENT_SALE:
        return isConsultationAccepted ? 'Hinweis Maklerempfehlung' : 'Hinweis Immobilienbewertung'
      case Intent.PARTIAL_SALE:
        return 'Hinweis Teilverkauf'
      case Intent.INDEPENDENT_WITH_OFFER_SALE:
        return 'Hinweis Käuferempfehlung'
      case Intent.INDEPENDENT_LET:
        return 'Hinweis Immobilienbewertung'
      default:
        return 'Hinweis Maklerempfehlung'
    }
  }

  useEffect(() => {
    if (visibility) {
      document.body.classList.add('modal-open')
      return
    }
    document.body.classList.remove('modal-open')
  }, [visibility])

  return (
    <Modal
      title={getTitle(intent, isConsultationAccepted)}
      visible={visibility}
      onClose={() => props.setVisibility(false)}
    >
      <div data-testid='user-consent-modal' className='padding-horizontal-l legal-text-container'>
        {getLegalText(realEstateType, intent, isConsultationAccepted)}
      </div>
    </Modal>
  )
}

const consentUserIntentSell = (
  <section data-testid='consent-user-intent-sell'>
    <p>
      Deine Daten werden zur Erstellung personalisierter Angebote und zur telefonischen Vereinbarung eines
      Beratungstermins an maximal 3 mit ImmoScout24 kooperierende <MaklerLink /> weitergegeben. Eine Liste dieser Makler
      erhältst du nach Übermittlung der Daten per E-Mail zugesandt. Um eine individuelle Beratung zu gewährleisten,
      benötigen wir deine E-Mail Adresse und Telefonnummer. Der Übermittlung deiner Daten kannst du selbstverständlich
      jederzeit widersprechen. Weitere Informationen hierzu findest du in unserer{' '}
      <DataProtectionInformationLink params='cmp_id=10-05081&cmp_name=residential_agb&cmp_position=residential_realtorleadengine&cmp_creative=rle_widget_legal-link' />
      . Es kann sein, dass zur Klärung deines Empfehlungsanliegens zunächst dein persönlicher ImmoScout24 Berater
      telefonisch Kontakt zu dir aufnimmt. Manche Empfehlungsanliegen leiten wir mit deinem ausdrücklichen
      Einverständnis auch zur Weitervermittlung an unsere Kooperationspartner immoverkauf24 GmbH, Gasstraße 18, 22761
      Hamburg weiter. Nähere Informationen erhältst du dann vorab telefonisch von deinem ImmoScout24-Berater.
      ImmoScout24 leitet deine Daten ausschließlich an professionelle Makler weiter, die Expertise aus dem Markt
      mitbringen und von ImmoScout24 vertraglich auf eine vertrauliche Behandlung deiner Daten verpflichtet wurden. Für
      die Übermittlung deiner Anfrage erhält ImmoScout24 eine Vergütung.
    </p>
  </section>
)

const consentUserIntentIndependentSell = (
  <section data-testid='consent-user-intent-independent-sale'>
    <p>
      Zur Erstellung einer Immobilienbewertung und unverbindlichen Verkaufsberatung geben wir deine Daten an einen mit
      ImmoScout24 <AnbieterLink /> weiter. Du erhältst den konkreten Empfänger deiner Daten per E-Mail zugesandt. Wenn
      du eine Weiterleitung deiner Daten bzw. eine Kontaktaufnahme durch diese nicht wünscht, kannst du unserem
      Vermittlungsservice jederzeit widersprechen. Bitte beachte, dass die Bereitstellung deiner Kontaktdaten für die
      von uns bzw. unserer Kooperationspartner angebotenen Leistungen im Sinne des Art. 6 Abs. 1 lit. B DSGVO
      (Vermittlungsservice bzw. Kontaktaufnahme und Angebotserstellung) erforderlich ist. Weitere Informationen hierzu
      findest du in unserer <DataProtectionInformationLink />. Ohne vollständige Angabe deiner Kontaktdaten können
      unsere Kooperationspartner für dich keine Immobilienbewertung erstellen. ImmoScout24 leitet deine Daten
      ausschließlich an professionelle Immobilienexperten weiter, die Expertise aus dem Markt mitbringen und von
      ImmoScout24 vertraglich auf eine vertrauliche Behandlung deiner Daten verpflichtet wurden. Für die Übermittlung
      deiner Anfrage erhält ImmoScout24 eine Vergütung.
    </p>
  </section>
)

const consentUserIntentIndependentSellAndConsultation = (
  <section data-testid='consent-user-intent-independent-sale-and-consultation'>
    <p>
      Deine Daten werden zur Erstellung personalisierter Angebote und zur telefonischen Vereinbarung eines
      Beratungstermins an maximal 3 mit ImmoScout24 kooperierende <MaklerLink /> weitergegeben. Eine Liste dieser Makler
      erhältst du nach Übermittlung der Daten per E-Mail zugesandt. Um eine individuelle Beratung zu gewährleisten,
      benötigen wir deine E-Mail Adresse und Telefonnummer. Der Übermittlung deiner Daten kannst du selbstverständlich
      jederzeit widersprechen. Weitere Informationen hierzu findest du in unserer{' '}
      <DataProtectionInformationLink params='cmp_id=10-05081&cmp_name=residential_agb&cmp_position=residential_realtorleadengine&cmp_creative=rle_widget_legal-link' />
      . Es kann sein, dass zur Klärung deines Empfehlungsanliegens zunächst dein persönlicher ImmoScout24 Berater
      telefonisch Kontakt zu dir aufnimmt. Manche Empfehlungsanliegen leiten wir mit deinem ausdrücklichen
      Einverständnis auch zur Weitervermittlung an unsere Kooperationspartner immoverkauf24 GmbH, Gasstraße 18, 22761
      Hamburg weiter. Nähere Informationen erhältst du dann vorab telefonisch von deinem ImmoScout24-Berater.
      ImmoScout24 leitet deine Daten ausschließlich an professionelle Makler weiter, die Expertise aus dem Markt
      mitbringen und von ImmoScout24 vertraglich auf eine vertrauliche Behandlung deiner Daten verpflichtet wurden. Für
      die Übermittlung deiner Anfrage erhält ImmoScout24 eine Vergütung.
    </p>
  </section>
)

const consentUserIntentIndependentSellPartial = (
  <section data-testid='consent-user-intent-partial-sale'>
    <p>
      Deine Daten werden zur Erstellung eines personalisierten Angebots und zur telefonischen Beratung an unsere
      Kooperationspartner Deutsche Teilkauf GmbH (Hohenzollernring 79-83, 50672 Köln), GNIW Gesellschaft für Nachhaltige
      Immobilienwirtschaft mbH (Kurfürstendamm 194, 10707 Berlin) oder EV LiquidHome GmbH - Lizenzpartner der Engel &
      Völkers Marken GmbH & Co. KG (Vancouverstraße 2a, 20457 Hamburg) weitergeleitet. Um eine individuelle Beratung zu
      gewährleisten, benötigen wir deine E-Mail Adresse und Telefonnummer. Der Übermittlung deiner Daten kannst du
      selbstverständlich jederzeit widersprechen. Weitere Informationen hierzu findest du in unserer{' '}
      <DataProtectionInformationLink />. Für die Übermittlung deiner Anfrage erhält ImmoScout24 eine Vergütung.
    </p>
  </section>
)

const consentUserIntentIndependentSellWithOffer = (
  <section data-testid='consent-user-intent-independent-with-offer-sale'>
    <p>
      Ihre Daten werden zur Erstellung personalisierter Angebote und zur möglichen Vereinbarung eines Beratungstermins
      an maximal drei mit ImmoScout24 kooperierende{' '}
      <a href=' https://www.immobilienscout24.de/neubau/bautraeger.html' target='_blank' rel='noreferrer noopener'>
        Bauträger
      </a>{' '}
      oder Fertighaus/Massisvhaushersteller weitergegeben, welche ein Kaufinteresse an Ihrem Objekt haben. Eine Liste
      dieser Kaufinteressenten erhalten Sie nach einer individuellen Kontaktaufnahme durch ImmoScout24. Um eine
      individuelle Beratung zu gewährleisten, werden Ihre E-Mail Adresse und Telefonnummer benötigt. Weitere
      Informationen hierzu finden Sie in unserer <DataProtectionInformationLink />. Wenn Sie eine Weiterleitung Ihrer
      Daten bzw. eine Kontaktaufnahme durch diese nicht wünschen, können Sie unserem Vermittlungsservice jederzeit
      widersprechen. Bitte beachten Sie, dass die Bereitstellung Ihrer Kontaktdaten für die von uns bzw. unserer
      Kooperationspartner angebotenen Leistungen im Sinne des Art. 6 Abs. 1 lit. B DSGVO (Vermittlungsservice bzw.
      Kontaktaufnahme und Angebotserstellung) erforderlich ist. ImmoScout24 leitet Ihre Daten ausschließlich an
      professionelle Bauträger oder Fertighaus/Massisvhaushersteller weiter, die Expertise aus dem Markt mitbringen und
      von ImmoScout24 vertraulich behandeln. Für die Übermittlung Ihrer Anfrage an passende Bauträger oder
      Fertighaus/Massisvhaushersteller, erhält ImmoScout24 eine Vergütung.
    </p>
  </section>
)

const consentUserIntentBuy = (
  <section data-testid='consent-user-intent-buy'>
    <p>
      Zur Erstellung einer Immobilienbewertung und unverbindlichen Kaufberatung geben wir deine Daten an maximal 3 mit
      ImmoScout24 kooperierende <MaklerLink /> weiter. Eine Liste dieser Makler erhältst du nach Übermittlung der Daten
      per E-Mail zugesandt. Um eine individuelle Beratung zu gewährleisten, benötigen wir deine E-Mail Adresse und
      Telefonnummer. Der Übermittlung deiner Daten kannst du selbstverständlich jederzeit widersprechen. Weitere
      Informationen hierzu findest du in unserer{' '}
      <DataProtectionInformationLink params='cmp_id=10-05081&cmp_name=residential_agb&cmp_position=residential_realtorleadengine&cmp_creative=rle_widget_legal-link' />
      . Es kann sein, dass zur Klärung deines Empfehlungsanliegens zunächst dein persönlicher ImmoScout24 Berater
      telefonisch Kontakt zu dir aufnimmt. Manche Empfehlungsanliegen leiten wir mit deinem ausdrücklichen
      Einverständnis auch zur Weitervermittlung an unsere Kooperationspartner immoverkauf24 GmbH, Gasstraße 18, 22761
      Hamburg weiter. Nähere Informationen erhältst du dann vorab telefonisch von deinem ImmoScout24-Berater.
      ImmoScout24 leitet deine Daten ausschließlich an professionelle Makler weiter, die Expertise aus dem Markt
      mitbringen und von ImmoScout24 vertraglich auf eine vertrauliche Behandlung deiner Daten verpflichtet wurden. Für
      die Übermittlung deiner Anfrage erhält ImmoScout24 eine Vergütung.
    </p>
  </section>
)

const consentUserIntentExtendedBuy = (
  <section data-testid='consent-user-intent-extended-buy'>
    <p>
      Deine Daten werden zur Erstellung personalisierter Angebote und zur telefonischen Vereinbarung eines
      Beratungstermins an maximal 5 mit ImmoScout24 kooperierende <MaklerLink /> weitergegeben. Eine Liste dieser Makler
      erhältst du nach Übermittlung der Daten per E-Mail zugesandt. Um eine individuelle Beratung zu gewährleisten,
      benötigen wir deine E-Mail Adresse und Telefonnummer. Der Übermittlung deiner Daten kannst du selbstverständlich
      jederzeit widersprechen. Weitere Informationen hierzu findest du in unserer{' '}
      <DataProtectionInformationLink params='cmp_id=10-05081&cmp_name=residential_agb&cmp_position=residential_realtorleadengine&cmp_creative=rle_widget_legal-link' />
      . Es kann sein, dass zur Klärung deines Empfehlungsanliegens zunächst dein persönlicher ImmoScout24 Berater
      telefonisch Kontakt zu dir aufnimmt. Manche Empfehlungsanliegen leiten wir mit deinem ausdrücklichen
      Einverständnis auch zur Weitervermittlung an unsere Kooperationspartner immoverkauf24 GmbH, Gasstraße 18, 22761
      Hamburg weiter. Nähere Informationen erhältst du dann vorab telefonisch von deinem ImmoScout24-Berater.
      ImmoScout24 leitet deine Daten ausschließlich an professionelle Makler weiter, die Expertise aus dem Markt
      mitbringen und von ImmoScout24 vertraglich auf eine vertrauliche Behandlung deiner Daten verpflichtet wurden. Für
      die Übermittlung deiner Anfrage erhält ImmoScout24 eine Vergütung.
    </p>
  </section>
)

const consentUserIntentRentOut = (
  <section data-testid='consent-user-intent-rent-out'>
    <p>
      Ihre Daten werden zur Erstellung personalisierter Angebote und zur telefonischen Vereinbarung eines
      Beratungstermins an maximal 3 mit ImmoScout24{' '}
      <a
        target='_blank'
        rel='noreferrer noopener'
        href='https://www.immobilienscout24.de/immobilienmakler/kooperierende_immobilienexperten.html?cmp_id=10-051339&cmp_name=residential_agb&cmp_position=residential_realtorleadengine&cmp_creative=rle_widget_partner-link'
      >
        kooperierende Immobilienexperten
      </a>{' '}
      weitergegeben. Eine Liste dieser Kooperationspartner erhalten Sie nach Übermittlung der Daten per E-Mail
      zugesandt. Um eine individuelle Beratung zu gewährleisten, werden Ihre E-Mail Adresse und Telefonnummer benötigt.
      Weitere Informationen hierzu finden Sie in unserer{' '}
      <DataProtectionInformationLink params='cmp_id=10-05081&cmp_name=residential_agb&cmp_position=residential_realtorleadengine&cmp_creative=rle_widget_legal-link' />
      . Wenn Sie eine Weiterleitung Ihrer Daten bzw. eine Kontaktaufnahme durch diese nicht wünschen, können Sie unserem
      Vermittlungsservice jederzeit widersprechen. Bitte beachten Sie, dass die Bereitstellung Ihrer Kontaktdaten für
      die von uns bzw. unserer Kooperationspartner angebotenen Leistungen im Sinne des Art. 6 Abs. 1 lit. B DSGVO
      (Vermittlungsservice bzw. Kontaktaufnahme und Angebotserstellung) erforderlich ist.
    </p>
    <br />
    <p>
      ImmoScout24 leitet Ihre Daten ausschließlich an professionelle Immobilienexperten weiter, die Expertise aus dem
      Markt mitbringen und von ImmoScout24 vertraglich auf eine vertrauliche Behandlung Ihrer Daten verpflichtet wurden.
      Für die Übermittlung Ihrer Anfrage erhält ImmoScout24 eine Vergütung.
    </p>
  </section>
)

const consentUserIntentRentOutWithOrMaybeWithRealtor = (
  <section data-testid='consent-user-intent-rent-out-with-or-maybe-with-realtor'>
    <p>
      Deine Daten werden zur Erstellung personalisierter Angebote und zur telefonischen Vereinbarung eines
      Beratungstermins an maximal 3 mit ImmoScout24 kooperierende <MaklerLink /> weitergegeben. Eine Liste dieser Makler
      erhältst du nach Übermittlung der Daten per E-Mail zugesandt. Um eine individuelle Beratung zu gewährleisten,
      benötigen wir deine E-Mail Adresse und Telefonnummer. Der Übermittlung deiner Daten kannst du selbstverständlich
      jederzeit widersprechen. Weitere Informationen hierzu findest du in unserer{' '}
      <DataProtectionInformationLink params='cmp_id=10-05081&cmp_name=residential_agb&cmp_position=residential_realtorleadengine&cmp_creative=rle_widget_legal-link' />
      . Es kann sein, dass zur Klärung deines Empfehlungsanliegens zunächst dein persönlicher ImmoScout24 Berater
      telefonisch Kontakt zu dir aufnimmt. Manche Empfehlungsanliegen leiten wir mit deinem ausdrücklichen
      Einverständnis auch zur Weitervermittlung an unsere Kooperationspartner immoverkauf24 GmbH, Gasstraße 18, 22761
      Hamburg weiter. Nähere Informationen erhältst du dann vorab telefonisch von deinem ImmoScout24-Berater.
      ImmoScout24 leitet deine Daten ausschließlich an professionelle Makler weiter, die Expertise aus dem Markt
      mitbringen und von ImmoScout24 vertraglich auf eine vertrauliche Behandlung deiner Daten verpflichtet wurden. Für
      die Übermittlung deiner Anfrage erhält ImmoScout24 eine Vergütung.
    </p>
  </section>
)

const consentUserIntentRentOutWithoutRealtor = (
  <section data-testid='consent-user-intent-rent-out-without-realtor'>
    <p>
      Zur Erstellung einer Immobilienbewertung und unverbindlichen Beratung geben wir deine Daten an einen mit
      ImmoScout24 <AnbieterLink /> weiter. Du erhältst den konkreten Empfänger deiner Daten per E-Mail zugesandt. Wenn
      du eine Weiterleitung deiner Daten bzw. eine Kontaktaufnahme durch diese nicht wünscht, kannst du unserem
      Vermittlungsservice jederzeit widersprechen. Bitte beachte, dass die Bereitstellung deiner Kontaktdaten für die
      von uns bzw. unserer Kooperationspartner angebotenen Leistungen im Sinne des Art. 6 Abs. 1 lit. B DSGVO
      (Vermittlungsservice bzw. Kontaktaufnahme und Angebotserstellung) erforderlich ist. Weitere Informationen hierzu
      findest du in unserer <DataProtectionInformationLink />. Ohne vollständige Angabe deiner Kontaktdaten können
      unsere Kooperationspartner für dich keine Immobilienbewertung erstellen. ImmoScout24 leitet deine Daten
      ausschließlich an professionelle Immobilienexperten weiter, die Expertise aus dem Markt mitbringen und von
      ImmoScout24 vertraglich auf eine vertrauliche Behandlung deiner Daten verpflichtet wurden. Für die Übermittlung
      deiner Anfrage erhält ImmoScout24 eine Vergütung.
    </p>
  </section>
)

const consentUserIntentRent = (
  <section data-testid='consent-user-intent-rent'>
    <p>
      Deine Daten werden zur dauerhaften Bereitstellung der Bewertung und zur Erstellung personalisierter Angebote von
      ImmoScout24 gespeichert. Um eine individuelle Beratung zu gewährleisten, werden deine E-Mail Adresse und
      Telefonnummer benötigt. Weitere Informationen hierzu findest du in unserer{' '}
      <DataProtectionInformationLink params='cmp_id=10-05081&cmp_name=residential_agb&cmp_position=residential_realtorleadengine&cmp_creative=rle_widget_legal-link' />
      .
    </p>
  </section>
)

const consentUserIntentForCommercial = (
  <section data-testid='consent-user-intent-commercial'>
    <p>
      Deine Daten werden zur Erstellung personalisierter Angebote und zur telefonischen Vereinbarung eines
      Beratungstermins an maximal 3 mit ImmoScout24 <AnbieterLink /> weitergegeben. Eine Liste dieser Experten erhältst
      du nach Übermittlung der Daten per E-Mail zugesandt. Wenn du eine Weiterleitung deiner Daten bzw. eine
      Kontaktaufnahme durch diese nicht wünscht, kannst du unserem Vermittlungsservice jederzeit widersprechen. Bitte
      beachte, dass die Bereitstellung Ihrer Kontaktdaten für die von uns bzw. unserer Kooperationspartner angebotenen
      Leistungen im Sinne des Art. 6 Abs. 1 lit. B DSGVO (Vermittlungsservice bzw. Kontaktaufnahme und
      Angebotserstellung) erforderlich ist. Weitere Informationen hierzu findest du in unserer{' '}
      <DataProtectionInformationLink />
      . Ohne vollständige Angabe deiner Kontaktdaten können unsere Kooperationspartner für dich keine Angebote oder
      Beratungstermine zur Verfügung stellen.
      <br />
    </p>
    <p>
      ImmoScout24 leitet deine Daten ausschließlich an professionelle Immobilienexperten weiter, die Expertise aus dem
      Markt mitbringen und von ImmoScout24 vertraglich auf eine vertrauliche Behandlung deiner Daten verpflichtet
      wurden. Für die Übermittlung deiner Anfrage erhält ImmoScout24 eine Vergütung.
    </p>
  </section>
)

const consentUserIntentForImmoverkauf = (
  <section data-testid='consent-user-immoverkauf'>
    <p>
      Ihre Daten werden zur Erstellung einer auf Sie zugeschnittenen Bewertung und zur telefonischen Vereinbarung eines
      Beratungstermins an maximal 3 mit immoverkauf24 kooperierende Immobilienexperten weitergegeben. Eine Liste dieser
      Immobilienexperten erhalten Sie nach Übermittlung der Daten per E-Mail zugesandt. Sofern immoverkauf24 keine für
      Sie passenden Immobilienexperten vermitteln kann, geben wir Ihre Daten zur Weitervermittlung an ImmoScout24
      (Immobilien Scout GmbH, Invalidenstr. 65, 10557 Berlin) oder an mit der Immobilen Scout GmbH kooperierenden{' '}
      <AnbieterLink displayText='Makler' /> weiter.Es kann sein, dass zur Klärung Ihres Anliegens zunächst ein
      persönlicher Berater telefonisch Kontakt zu Ihnen aufnimmt. Um eine individuelle Beratung zu gewährleisten,
      benötigen wir Ihre E-Mail Adresse und Telefonnummer. Der Übermittlung Ihrer Daten können Sie selbstverständlich
      jederzeit widersprechen. Weitere Informationen finden Sie in unserer <DataProtectionInformationLink />.
    </p>
    <br />
    <p>
      immoverkauf24 leitet Ihre Daten ausschließlich an professionelle Immobilienexperten weiter, die Expertise aus dem
      Markt mitbringen und von immoverkauf24 vertraglich auf eine vertrauliche Behandlung Ihrer Daten verpflichtet
      wurden. Für die Übermittlung Ihrer Anfrage erhält immoverkauf24 eine Vergütung.
    </p>
  </section>
)

const consentUserSolarIntent = (
  <section data-testid='consent-user-intent-solar'>
    <p>
      Ihre Daten werden zur Erstellung personalisierter Angebote und zur telefonischen Vereinbarung eines
      Beratungstermins an maximal ein mit ImmoScout24 kooperierendes Solarunternehmen oder an unseren Partner WattFox
      GmbH weitergegeben. Das Unternehmen das ihre Daten erhält wird Ihnen nach Übermittlung der Daten per E-Mail
      genannt. Um eine individuelle Beratung zu gewährleisten, benötigen wir Ihre E-Mail Adresse und Telefonnummer. Der
      Übermittlung Ihrer Daten können Sie selbstverständlich jederzeit widersprechen. Weitere Informationen hierzu
      finden Sie in unserer <DataProtectionInformationLink />. Es kann sein, dass zur Klärung Ihres Empfehlungsanliegens
      zunächst Ihr persönlicher ImmoScout24 oder Wattfox Berater telefonisch Kontakt zu Ihnen aufnimmt. ImmoScout24
      leitet Ihre Daten ausschließlich an professionelle Solarunternehmen oder die Wattfox GmbH weiter, die Expertise
      aus dem Markt mitbringen und von ImmoScout24 vertraglich auf eine vertrauliche Behandlung Ihrer Daten verpflichtet
      wurden. Für die Übermittlung Ihrer Anfrage erhält ImmoScout24 eine Vergütung.
    </p>
  </section>
)

export default UserConsentModal
